<template>
    <div class="voice-agent-page-content">
        <div class="header-section">
            <div class="title-wrapper">
                <h1 class="main-title">Arama Geçmişi</h1>
                <p class="title-description">
                    Bu sayfada ses ajanları tarafından yapılan aramaların geçmişini görüntüleyebilirsiniz
                </p>
            </div>
        </div>
        <div class="section">
            <div class="toolbar-container">
                <CallHistoryFilterToolbar v-model="filters" :status-list="[]" ref="filterToolbar" />
                <div class="toolbar-group">
                    <div class="simple-pagination">
                        <a-button icon="double-left" @click="firstPage" :disabled="currentPage <= 1 || loading" />
                        <a-button icon="left" @click="previousPage" :disabled="currentPage <= 1 || loading" />
                        <span class="page-number">{{ currentPage }}</span>
                        <a-button icon="right" @click="nextPage" :disabled="!hasNextPage || loading" />
                    </div>

                    <a-button @click="showCustomizeColumnsModal" class="toolbar-button">
                        <i class="material-icons">settings</i>
                        <span>Customize Field</span>
                    </a-button>

                    <a-button type="primary" icon="export" @click="exportData"> Export </a-button>
                </div>
            </div>

            <div class="table-wrapper">
                <a-spin :spinning="loading">
                    <SupsisTable
                        :columns="getTableColumns()"
                        :rows="calls"
                        :options="{
                            rowStyleOption: { stripe: true },
                            rowKeyFieldName: 'call_id',
                            fixedHeader: true,
                            borderX: true,
                            borderY: true,
                        }"
                    />
                </a-spin>
            </div>
        </div>

        <!-- Transcript Modal -->
        <a-modal
            v-model="transcriptModalVisible"
            title="Arama Detayları"
            width="1000px"
            :footer="null"
            :bodyStyle="{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }"
            @cancel="closeTranscriptModal"
        >
            <div v-if="selectedCall">
                <div class="call-details">
                    <div class="call-info">
                        <div><strong>Call ID:</strong> {{ selectedCall.call_id }}</div>
                        <div><strong>Time:</strong> {{ formatDate(selectedCall.start_timestamp) }}</div>
                        <div>
                            <strong>Duration:</strong>
                            {{ formatDuration(selectedCall.start_timestamp, selectedCall.end_timestamp) }}
                        </div>
                        <div><strong>From:</strong> {{ selectedCall.from_number }}</div>
                        <div><strong>To:</strong> {{ selectedCall.to_number }}</div>
                        <div><strong>Status:</strong> {{ selectedCall.call_status }}</div>
                    </div>

                    <div class="transcript-container">
                        <h3>Transcript</h3>
                        <div class="transcript-content">
                            <div
                                v-for="(item, index) in transcriptItems"
                                :key="index"
                                :class="['transcript-item', 'transcript-' + item.role]"
                                @click="seekToTranscriptItem(item, index)"
                            >
                                <!-- User Message -->
                                <template v-if="item.role === 'user'">
                                    <div class="message-header">
                                        <i class="material-icons">person</i>
                                        <span class="role-label">Kullanıcı</span>
                                        <span class="time-label" v-if="item.words && item.words.length">{{ formatSeconds(item.words[0].start) }}</span>
                                    </div>
                                    <div class="message-content">{{ item.content }}</div>
                                </template>

                                <!-- Agent Message -->
                                <template v-else-if="item.role === 'agent'">
                                    <div class="message-header">
                                        <i class="material-icons">smart_toy</i>
                                        <span class="role-label">Ajan</span>
                                        <span class="time-label" v-if="item.words && item.words.length">{{ formatSeconds(item.words[0].start) }}</span>
                                    </div>
                                    <div class="message-content">{{ item.content }}</div>
                                </template>

                                <!-- Tool Call Invocation -->
                                <template v-else-if="item.role === 'tool_call_invocation'">
                                    <div class="tool-call-box invocation">
                                        <div class="tool-call-header">
                                            <i class="material-icons">{{ getToolIcon(item.type) }}</i>
                                            <span class="tool-name">{{ item.name }}</span>
                                            <a-tag :color="getToolTypeColor(item.type)" size="small">{{ getToolTypeText(item.type) }}</a-tag>
                                            <span class="time-label">{{ formatSeconds(item.time_sec) }}</span>
                                            <a 
                                                v-if="getAutomationLink(item)" 
                                                :href="getAutomationLink(item)" 
                                                target="_blank" 
                                                class="automation-link"
                                                @click.stop
                                            >
                                                <i class="material-icons">open_in_new</i>
                                                Otomasyonu Düzenle
                                            </a>
                                        </div>
                                        <div class="tool-call-arguments" v-if="item.arguments">
                                            <pre>{{ formatToolArguments(item.arguments) }}</pre>
                                        </div>
                                    </div>
                                </template>

                                <!-- Tool Call Result -->
                                <template v-else-if="item.role === 'tool_call_result'">
                                    <div class="tool-call-box result" :class="{ success: item.successful, error: !item.successful }">
                                        <div class="tool-call-header">
                                            <i class="material-icons">{{ item.successful ? 'check_circle' : 'error' }}</i>
                                            <span class="result-label">{{ item.successful ? 'Başarılı' : 'Hata' }}</span>
                                            <span class="time-label">{{ formatSeconds(item.time_sec) }}</span>
                                        </div>
                                        <div class="tool-call-content" v-if="item.content">
                                            <pre>{{ item.content }}</pre>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>

                    <div v-if="$_.get(selectedCall, 'call_analysis.call_summary')" class="call-summary">
                        <h3>Call Summary</h3>
                        <p>{{ $_.get(selectedCall, "call_analysis.call_summary") }}</p>
                    </div>

                    <div v-if="selectedCall.recording_url" class="audio-player">
                        <h3>Recording</h3>
                        <div class="modern-audio-player">
                            <div class="audio-player-info">
                                <i class="material-icons audio-icon pulse">music_note</i>
                                <div class="audio-title-container">
                                    <div class="audio-title">{{ formatDate(selectedCall.start_timestamp) }}</div>
                                </div>
                            </div>
                            <div class="audio-player-wrapper">
                                <audio
                                    controls
                                    :src="selectedCall.recording_url"
                                    style="width: 100%"
                                    ref="transcriptAudioPlayer"
                                ></audio>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </a-modal>

        <!-- Customize Columns Modal -->
        <a-modal
            v-model="customizeColumnsModalVisible"
            title="Customize Fields"
            @ok="saveColumnSettings"
            okText="Kaydet"
            cancelText="İptal"
        >
            <div class="customize-columns-container">
                <div 
                    class="column-option" 
                    v-for="column in availableColumns" 
                    :key="column.key"
                    @click="toggleColumnVisibility(column.key)"
                >
                    <a-switch
                        :checked="columnVisibility[column.key]"
                        :disabled="column.key === 'time' || column.key === 'actions'"
                        @click.native.stop
                        @change="(checked) => onSwitchChange(column.key, checked)"
                    />
                    <span class="column-label">{{ column.title }}</span>
                </div>
            </div>
        </a-modal>

        <!-- Audio Player Bottom Dock -->
        <div class="audio-player-dock" v-if="showAudioPlayer">
            <div class="audio-player-container">
                <div class="audio-player-info">
                    <i class="material-icons audio-icon pulse">music_note</i>
                    <div class="audio-title-container">
                        <div class="now-playing">Şimdi Çalıyor</div>
                        <div class="audio-title">{{ currentPlayingRecordName }}</div>
                    </div>
                </div>
                <div class="audio-player-wrapper">
                    <audio controls :src="currentPlayingRecordURL" autoplay ref="audioPlayer"></audio>
                </div>
                <div class="audio-player-controls">
                    <a-button type="danger" shape="circle" icon="close" size="large" @click="closeAudioPlayer" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { VOICE_AGENT_API } from "../../../../../api/voiceAgent";
import _ from "lodash";
import moment from "moment";
import CallHistoryFilterToolbar from "../components/CallHistoryFilterToolbar.vue";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";

export default {
    name: "VoiceAgentCallHistory",
    components: {
        CallHistoryFilterToolbar,
        SupsisTable,
        SupsisButton,
    },
    props: {
        channelId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            calls: [],
            agents: [],
            loading: false,
            transcriptModalVisible: false,
            customizeColumnsModalVisible: false,
            selectedCall: null,

            // Audio player dock
            showAudioPlayer: false,
            currentPlayingRecordURL: "",
            currentPlayingRecordName: "",

            // Transcript zaman bilgilerini tutmak için dizi
            transcriptTimestamps: [],

            // Filter state
            filters: [],

            // Pagination state
            currentPage: 1,
            pageSize: 10,
            hasNextPage: true,

            // filterMetas değişkeni (addFilter metodu için)
            filterMetas: [
                {
                    name: "batch_call_id",
                    label: "Toplu Arama ID",
                    materialIcon: "batch_prediction",
                    controlType: "text-input",
                },
                {
                    name: "dateFilter",
                    label: "Arama Tarihi",
                    materialIcon: "event",
                },
                {
                    name: "callType",
                    label: "Arama Türü",
                    materialIcon: "call",
                },
                {
                    name: "callDirection",
                    label: "Arama Yönü",
                    materialIcon: "swap_horiz",
                },
                {
                    name: "agentId",
                    label: "Sesli Ajan",
                    materialIcon: "support_agent",
                },
                {
                    name: "callStatus",
                    label: "Arama Durumu",
                    materialIcon: "info",
                },
                {
                    name: "disconnectionReason",
                    label: "Bağlantı Kesilme Nedeni",
                    materialIcon: "call_end",
                },
                {
                    name: "userSentiment",
                    label: "Kullanıcı Duygu Durumu",
                    materialIcon: "mood",
                },
            ],

            // Columns configuration
            columnVisibility: {
                time: true,
                callDuration: true,
                type: true,
                direction: true,
                cost: true,
                callID: true,
                disconnectionReason: true,
                callStatus: true,
                userSentiment: true,
                from: true,
                to: true,
                agent: false,
                callSuccessful: false,
                latency: false,
                actions: true,
            },

            availableColumns: [
                { title: "Time", dataIndex: "start_timestamp", key: "time", scopedSlots: { customRender: "time" } },
                {
                    title: "Call Duration",
                    dataIndex: "callDuration",
                    key: "callDuration",
                    scopedSlots: { customRender: "callDuration" },
                },
                { title: "Type", dataIndex: "call_type", key: "type", scopedSlots: { customRender: "type" } },
                { title: "Direction", dataIndex: "direction", key: "direction", scopedSlots: { customRender: "direction" } },
                { title: "Cost", dataIndex: "cost", key: "cost", scopedSlots: { customRender: "cost" } },
                { title: "Call ID", dataIndex: "call_id", key: "callID", scopedSlots: { customRender: "callID" } },
                {
                    title: "Disconnection Reason",
                    dataIndex: "disconnection_reason",
                    key: "disconnectionReason",
                    scopedSlots: { customRender: "disconnectionReason" },
                },
                {
                    title: "Call Status",
                    dataIndex: "call_status",
                    key: "callStatus",
                    scopedSlots: { customRender: "callStatus" },
                },
                {
                    title: "User Sentiment",
                    dataIndex: "user_sentiment",
                    key: "userSentiment",
                    scopedSlots: { customRender: "userSentiment" },
                },
                { title: "From", dataIndex: "from_number", key: "from", scopedSlots: { customRender: "from" } },
                { title: "To", dataIndex: "to_number", key: "to", scopedSlots: { customRender: "to" } },
                { title: "Agent", dataIndex: "agent_id", key: "agent", scopedSlots: { customRender: "agent" } },
                {
                    title: "Call Successful",
                    dataIndex: "call_successful",
                    key: "callSuccessful",
                    scopedSlots: { customRender: "callSuccessful" },
                },
                {
                    title: "End to End Latency",
                    dataIndex: "latency",
                    key: "latency",
                    scopedSlots: { customRender: "latency" },
                },
                {
                    title: "Actions",
                    key: "actions",
                    fixed: "right",
                    width: 100,
                    scopedSlots: { customRender: "actions" },
                },
            ],
        };
    },
    computed: {
        visibleColumns() {
            return this.availableColumns.filter((col) => this.columnVisibility[col.key]);
        },
        transcriptItems() {
            if (!this.selectedCall) return [];
            // Önce transcript_with_tool_calls'ı dene, yoksa eski transcript'i kullan
            if (this.selectedCall.transcript_with_tool_calls && Array.isArray(this.selectedCall.transcript_with_tool_calls)) {
                return this.selectedCall.transcript_with_tool_calls;
            }
            // Fallback: eski transcript formatı
            if (this.selectedCall.transcript) {
                return this.selectedCall.transcript.split("\n").map(function(line) {
                    return { role: 'agent', content: line };
                });
            }
            return [];
        },
    },
    watch: {
        // Modal açıldığında/kapatıldığında ses oynatıcılarını kontrol et
        transcriptModalVisible(newVal) {
            if (!newVal) {
                // Modal kapandığında modaldaki ses oynatıcıyı durdur
                if (this.$refs.transcriptAudioPlayer) {
                    this.$refs.transcriptAudioPlayer.pause();
                }
            } else {
                // Modal açıldığında dock audio player'ı durdur
                if (this.showAudioPlayer && this.$refs.audioPlayer) {
                    this.$refs.audioPlayer.pause();
                }
            }
        },
        "$route.query": {
            immediate: true,
            handler(newQuery) {
                if (newQuery.filter_batch_call_id) {
                    this.$nextTick(() => {
                        this.handleUrlParams();
                    });
                }
            },
        },
        filters: {
            handler() {
                this.currentPage = 1;
                this.fetchCalls();
            },
            deep: true,
        },
    },
    methods: {
        getTableColumns() {
            var self = this;
            var columns = [];

            if (this.columnVisibility.time) {
                columns.push({
                    key: "time",
                    field: "start_timestamp",
                    title: "Time",
                    width: 150,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        return h("span", self.formatDate(row.start_timestamp));
                    },
                });
            }

            if (this.columnVisibility.callDuration) {
                columns.push({
                    key: "callDuration",
                    field: "callDuration",
                    title: "Call Duration",
                    width: 120,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        if (row.start_timestamp && row.end_timestamp) {
                            return h("span", self.formatDuration(row.start_timestamp, row.end_timestamp));
                        }
                        return h("span", "-");
                    },
                });
            }

            if (this.columnVisibility.type) {
                columns.push({
                    key: "type",
                    field: "call_type",
                    title: "Type",
                    width: 120,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        return h("a-tag", { props: { color: row.call_type === "phone_call" ? "blue" : "green" } }, row.call_type);
                    },
                });
            }

            if (this.columnVisibility.direction) {
                columns.push({
                    key: "direction",
                    field: "direction",
                    title: "Direction",
                    width: 110,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        var isInbound = row.direction === "inbound";
                        var label = isInbound ? "Inbound" : "Outbound";
                        var color = isInbound ? "cyan" : "purple";
                        var icon = isInbound ? "phone-incoming" : "phone-outgoing";
                        return h("a-tag", { props: { color: color } }, [
                            h("a-icon", { props: { type: icon }, style: { marginRight: "4px" } }),
                            label
                        ]);
                    },
                });
            }

            if (this.columnVisibility.cost) {
                columns.push({
                    key: "cost",
                    field: "cost",
                    title: "Cost",
                    width: 100,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        return h("span", self.formatCost(row) + " ₺");
                    },
                });
            }

            if (this.columnVisibility.callID) {
                columns.push({
                    key: "callID",
                    field: "call_id",
                    title: "Call ID",
                    width: 150,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        return h("a-tooltip", { props: { title: row.call_id } }, self.truncateString(row.call_id, 15));
                    },
                });
            }

            if (this.columnVisibility.disconnectionReason) {
                columns.push({
                    key: "disconnectionReason",
                    field: "disconnection_reason",
                    title: "Disconnection Reason",
                    width: 180,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        var description = self.getDisconnectionReasonDescription(row.disconnection_reason);
                        var tag = h("a-tag", { props: { color: self.getDisconnectionReasonColor(row.disconnection_reason) } }, self.formatDisconnectionReason(row.disconnection_reason));
                        if (description) {
                            return h("a-tooltip", { props: { title: description } }, [tag]);
                        }
                        return tag;
                    },
                });
            }

            if (this.columnVisibility.callStatus) {
                columns.push({
                    key: "callStatus",
                    field: "call_status",
                    title: "Call Status",
                    width: 120,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        return h("a-tag", { props: { color: self.getCallStatusColor(row.call_status) } }, row.call_status);
                    },
                });
            }

            if (this.columnVisibility.userSentiment) {
                columns.push({
                    key: "userSentiment",
                    field: "user_sentiment",
                    title: "User Sentiment",
                    width: 140,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        var sentiment = self.$_.get(row, "call_analysis.user_sentiment", "Unknown");
                        return h("a-tag", { props: { color: self.getSentimentColor(sentiment) } }, sentiment);
                    },
                });
            }

            if (this.columnVisibility.from) {
                columns.push({
                    key: "from",
                    field: "from_number",
                    title: "From",
                    width: 140,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        return h("span", row.from_number);
                    },
                });
            }

            if (this.columnVisibility.to) {
                columns.push({
                    key: "to",
                    field: "to_number",
                    title: "To",
                    width: 140,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        return h("span", row.to_number);
                    },
                });
            }

            if (this.columnVisibility.agent) {
                columns.push({
                    key: "agent",
                    field: "agent_id",
                    title: "Agent",
                    width: 150,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        return h("span", self.getAgentName(row.agent_id));
                    },
                });
            }

            if (this.columnVisibility.callSuccessful) {
                columns.push({
                    key: "callSuccessful",
                    field: "call_successful",
                    title: "Call Successful",
                    width: 130,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        var successful = self.$_.get(row, "call_analysis.call_successful");
                        return h("a-tag", { props: { color: successful ? "green" : "red" } }, successful ? "Yes" : "No");
                    },
                });
            }

            if (this.columnVisibility.latency) {
                columns.push({
                    key: "latency",
                    field: "latency",
                    title: "End to End Latency",
                    width: 150,
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        var latency = self.$_.get(row, "latency.e2e.p50");
                        return h("span", latency ? Math.round(latency) + "ms" : "-");
                    },
                });
            }

            if (this.columnVisibility.actions) {
                columns.push({
                    key: "actions",
                    field: "actions",
                    title: "Actions",
                    width: 100,
                    fixed: "right",
                    renderBodyCell: function(_ref, h) {
                        var row = _ref.row;
                        var buttons = [];
                        if (row.recording_url) {
                            buttons.push(h("a-button", {
                                props: { type: "link", icon: "sound" },
                                on: { click: function() { self.playRecording(row); } },
                            }));
                        }
                        if (row.transcript || row.transcript_with_tool_calls) {
                            buttons.push(h("a-button", {
                                props: { type: "link", icon: "file-text" },
                                on: { click: function() { self.showTranscript(row); } },
                            }));
                        }
                        return h("div", buttons);
                    },
                });
            }

            return columns;
        },
        onSwitchChange(key, checked) {
            if (key === 'time' || key === 'actions') return;
            this.columnVisibility[key] = checked;
        },
        loadSettings() {
            const savedColumnSettings = localStorage.getItem("call-history-columns");
            if (savedColumnSettings) {
                try {
                    const parsedSettings = JSON.parse(savedColumnSettings);
                    this.columnVisibility = { ...this.columnVisibility, ...parsedSettings };
                } catch (error) {
                    console.error("Kolon ayarları yüklenirken hata oluştu:", error);
                }
            }
        },

        showCustomizeColumnsModal() {
            this.customizeColumnsModalVisible = true;
        },

        saveColumnSettings() {
            localStorage.setItem("call-history-columns", JSON.stringify(this.columnVisibility));
            this.customizeColumnsModalVisible = false;
        },

        toggleColumnVisibility(key) {
            // Time ve actions kolonları için switch etkinleştirme işlemi yapma
            if (key === 'time' || key === 'actions') return;
            
            // Diğer kolonlar için mevcut durumu tersine çevir
            this.columnVisibility[key] = !this.columnVisibility[key];
        },

        firstPage() {
            if (this.currentPage > 1) {
                this.currentPage = 1;
                this.fetchCalls();
            }
        },

        previousPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.fetchCalls();
            }
        },

        nextPage() {
            if (this.hasNextPage) {
                this.currentPage++;
                this.fetchCalls();
            }
        },

        async fetchCalls() {
            try {
                this.loading = true;

                // Build filter criteria object
                const options = {
                    filter_criteria: {},
                    sort_order: "descending",
                    limit: this.pageSize,
                };

                // Filtreleri işle
                this.processFilters(options);

                // Loglama ekle
                console.log("API'ye gönderilen filtreler:", JSON.stringify(options.filter_criteria, null, 2));

                // Sayfalama için pagination_key kullan
                if (this.currentPage > 1 && this.calls.length > 0) {
                    // Son call_id'yi pagination_key olarak kullan
                    const lastCall = this.calls[this.calls.length - 1];
                    options.pagination_key = lastCall.call_id;
                }

                // API isteği gönderme
                const response = await VOICE_AGENT_API.calls.list(options);

                const newCalls = response.items || [];
                console.log("API'den dönen çağrılar:", newCalls.length);

                // Boş array gelirse veya az sayıda kayıt gelirse daha fazla sayfa olmadığını anlıyoruz
                this.hasNextPage = newCalls.length === this.pageSize;
                this.calls = newCalls;
            } catch (error) {
                console.error("Arama geçmişi çekilirken hata oluştu:", error);
                this.$message.error("Arama geçmişi yüklenirken bir hata oluştu");
            } finally {
                this.loading = false;
            }
        },

        processFilters(options) {
            this.filters.forEach((filter) => {
                if (filter.name === "dateFilter" && filter.filterValue) {
                    if (filter.filterValue.startDate && filter.filterValue.endDate) {
                        options.filter_criteria.start_timestamp = {
                            lower_threshold: moment(filter.filterValue.startDate).valueOf(),
                            upper_threshold: moment(filter.filterValue.endDate).valueOf(),
                        };
                    }
                } else if (filter.name === "batch_call_id" && filter.filterValue) {
                    // batch_call_id'yi string[] formatında gönder
                    options.filter_criteria.batch_call_id = [filter.filterValue];
                    console.log("BATCH_CALL_ID filtresi eklendi:", [filter.filterValue]);
                } else if (filter.name === "callType" && filter.filterValue) {
                    if (filter.filterValue.length > 0) {
                        options.filter_criteria.call_type = [].concat(filter.filterValue);
                    }
                } else if (filter.name === "callDirection" && filter.filterValue) {
                    if (filter.filterValue.length > 0) {
                        options.filter_criteria.direction = [].concat(filter.filterValue);
                    }
                } else if (filter.name === "agentId" && filter.filterValue) {
                    options.filter_criteria.agent_id = [filter.filterValue];
                } else if (filter.name === "callStatus" && filter.filterValue) {
                    if (filter.filterValue.length > 0) {
                        options.filter_criteria.call_status = [].concat(filter.filterValue);
                    }
                } else if (filter.name === "disconnectionReason" && filter.filterValue) {
                    if (filter.filterValue.length > 0) {
                        options.filter_criteria.disconnection_reason = [].concat(filter.filterValue);
                    }
                } else if (filter.name === "userSentiment" && filter.filterValue) {
                    if (filter.filterValue.length > 0) {
                        options.filter_criteria.user_sentiment = [].concat(filter.filterValue);
                    }
                }
            });
        },

        async fetchAgents() {
            try {
                const agents = await VOICE_AGENT_API.agents.list();
                this.agents = agents;
            } catch (error) {
                console.error("Ajanlar çekilirken hata oluştu:", error);
                this.$message.error("Ajanlar yüklenirken bir hata oluştu");
            }
        },

        // Formatting methods
        formatDate(timestamp) {
            if (!timestamp) return "-";
            return moment(timestamp).format("DD/MM/YYYY HH:mm");
        },

        formatDuration(startTimestamp, endTimestamp) {
            if (!startTimestamp || !endTimestamp) return "0:00";

            const duration = moment.duration(moment(endTimestamp).diff(moment(startTimestamp)));
            const minutes = Math.floor(duration.asMinutes());
            const seconds = Math.floor(duration.asSeconds() % 60);

            return `${minutes}:${seconds < 10 ? "0" + seconds : seconds}`;
        },

        formatCost(record) {
            console.log("formatCost", record);
            // Cost değerini %30 artırarak göster
            const price = this.$_.get(record, "call_cost.total_supsis_cost_as_tl", 0);
            return price.toFixed(2);
        },

        truncateString(str, length) {
            if (!str) return "";
            return str.length > length ? str.substring(0, length) + "..." : str;
        },

        formatDisconnectionReason(reason) {
            if (!reason) return "-";
            var labels = {
                user_hangup: "User Hangup",
                agent_hangup: "Agent Hangup",
                call_transfer: "Call Transfer",
                voicemail_reached: "Voicemail Reached",
                inactivity: "Inactivity",
                max_duration_reached: "Max Duration",
                dial_busy: "Dial Busy",
                dial_failed: "Dial Failed",
                dial_no_answer: "No Answer",
                invalid_destination: "Invalid Destination",
                telephony_provider_permission_denied: "Permission Denied",
                telephony_provider_unavailable: "Provider Unavailable",
                sip_routing_error: "SIP Routing Error",
                marked_as_spam: "Marked as Spam",
                user_declined: "User Declined",
                concurrency_limit_reached: "Concurrency Limit",
                no_valid_payment: "No Valid Payment",
                scam_detected: "Scam Detected",
                error_llm_websocket_open: "LLM Websocket Open Error",
                error_llm_websocket_lost_connection: "LLM Connection Lost",
                error_llm_websocket_runtime: "LLM Runtime Error",
                error_llm_websocket_corrupt_payload: "LLM Corrupt Payload",
                error_no_audio_received: "No Audio Received",
                error_asr: "ASR Error",
                error_retell: "Retell Error",
                error_unknown: "Unknown Error",
                error_user_not_joined: "User Not Joined",
                registered_call_timeout: "Call Timeout",
            };
            return labels[reason] || reason.replace(/_/g, " ");
        },

        getDisconnectionReasonDescription(reason) {
            if (!reason) return "";
            var descriptions = {
                user_hangup: "Expected behavior, user hangup the call.",
                agent_hangup: "Expected behavior, AI agent hangup the call.",
                call_transfer: "Expected behavior, AI agent transferred the call.",
                voicemail_reached: "Expected behavior, if AI agent is configured with voicemail settings, and voicemail is reached.",
                inactivity: "Expected behavior, call was terminated due to the 'end_call_after_silence_ms' setting reached after long inactivity.",
                max_duration_reached: "Expected behavior, call was terminated due to maximum duration reached.",
                dial_busy: "Outbound call not connected, the number dialed is busy.",
                dial_failed: "Outbound call not connected, dialing failed with no or unknown sip error code.",
                dial_no_answer: "Outbound call not connected, the number dialed did not answer.",
                invalid_destination: "Outbound call not connected, the number dialed is invalid.",
                telephony_provider_permission_denied: "Outbound call not connected, the sip trunk credentials are not authenticated.",
                telephony_provider_unavailable: "Outbound call not connected, the telephony provider is unavailable.",
                sip_routing_error: "Outbound call not connected, the sip routing is going over too many hops or is in a loop.",
                marked_as_spam: "Outbound call not connected, the number dialed is marked as spam.",
                user_declined: "Outbound call not connected, user declined the call.",
                concurrency_limit_reached: "Error, concurrency limit reached, add a retry with exponential backoff.",
                no_valid_payment: "Error, no valid payment registered on file, or service shut down due to bill overdue.",
                scam_detected: "Error, scam detected for that particular agent.",
                error_llm_websocket_open: "Error, LLM websocket did not open between Retell server and your backend.",
                error_llm_websocket_lost_connection: "Error, LLM websocket connection broke during the call.",
                error_llm_websocket_runtime: "Error, LLM websocket received a closing signal other than 1000 from your server.",
                error_llm_websocket_corrupt_payload: "Error, LLM websocket received unspecified payload.",
                error_no_audio_received: "Error, has not received audio from Twilio or web frontend for a while after connection has established.",
                error_asr: "Error, Retell's ASR encountered a problem.",
                error_retell: "Error, unspecified Retell side problem.",
                error_unknown: "Error, unknown error.",
                error_user_not_joined: "Error, user did not join web call within 30s after calling startWebCall.",
                registered_call_timeout: "Error, phone call is 30s or more apart from registration.",
            };
            return descriptions[reason] || "";
        },

        getDisconnectionReasonColor(reason) {
            if (!reason) return "default";

            // ended (expected behavior) - green
            var endedReasons = ["user_hangup", "agent_hangup", "call_transfer", "voicemail_reached", "inactivity", "max_duration_reached"];
            // not_connected - orange
            var notConnectedReasons = ["dial_busy", "dial_failed", "dial_no_answer", "invalid_destination", "telephony_provider_permission_denied", "telephony_provider_unavailable", "sip_routing_error", "marked_as_spam", "user_declined"];
            // error - red
            var errorReasons = ["concurrency_limit_reached", "no_valid_payment", "scam_detected", "error_llm_websocket_open", "error_llm_websocket_lost_connection", "error_llm_websocket_runtime", "error_llm_websocket_corrupt_payload", "error_no_audio_received", "error_asr", "error_retell", "error_unknown", "error_user_not_joined", "registered_call_timeout"];

            if (endedReasons.indexOf(reason) !== -1) return "green";
            if (notConnectedReasons.indexOf(reason) !== -1) return "orange";
            if (errorReasons.indexOf(reason) !== -1) return "red";

            return "default";
        },

        getCallStatusColor(status) {
            if (!status) return "default";

            const colorMap = {
                ended: "green",
                error: "red",
                registered: "blue",
                initiated: "purple",
                in_progress: "orange",
            };

            return colorMap[status] || "default";
        },

        getSentimentColor(sentiment) {
            if (!sentiment) return "default";

            const colorMap = {
                Neutral: "blue",
                Positive: "green",
                Negative: "red",
                Unknown: "default",
            };

            return colorMap[sentiment] || "default";
        },

        getAgentName(agentId) {
            if (!agentId) return "-";
            const agent = this.agents.find((a) => a.agent_id === agentId);
            return agent ? agent.agent_name : agentId;
        },

        // Action methods
        playRecording(record) {
            if (record.recording_url) {
                // Eğer detay modalı açıksa ve audio player çalışıyorsa durdur
                if (this.transcriptModalVisible && this.$refs.transcriptAudioPlayer) {
                    this.$refs.transcriptAudioPlayer.pause();
                }

                this.currentPlayingRecordURL = record.recording_url;
                this.currentPlayingRecordName =
                    this.formatDate(record.start_timestamp) + " - " + (record.from_number || "");
                this.showAudioPlayer = true;

                // Focus on audio player
                this.$nextTick(() => {
                    if (this.$refs.audioPlayer) {
                        this.$refs.audioPlayer.focus();
                    }
                });
            }
        },

        closeAudioPlayer() {
            // Pause audio and reset
            if (this.$refs.audioPlayer) {
                this.$refs.audioPlayer.pause();
            }
            this.showAudioPlayer = false;
            this.currentPlayingRecordURL = "";
            this.currentPlayingRecordName = "";
        },

        showTranscript(call) {
            this.selectedCall = call;

            // Eğer dock audio player açıksa kapat
            if (this.showAudioPlayer) {
                this.closeAudioPlayer();
            }

            this.transcriptModalVisible = true;

            // Modal açıldığında transcript timestamp'leri hazırla
            this.$nextTick(() => {
                this.prepareTranscriptTimestamps();
            });
        },

        closeTranscriptModal() {
            // Modal kapatılırken modaldaki ses oynatıcıyı durdur
            if (this.$refs.transcriptAudioPlayer) {
                this.$refs.transcriptAudioPlayer.pause();
            }
            this.transcriptModalVisible = false;
        },

        seekToTranscriptItem(item, index) {
            if (this.selectedCall && this.selectedCall.recording_url && this.$refs.transcriptAudioPlayer) {
                var timeToSeek = null;

                // Role'e göre zaman bilgisini al
                if (item.role === 'user' || item.role === 'agent') {
                    if (item.words && item.words.length > 0) {
                        timeToSeek = item.words[0].start;
                    }
                } else if (item.role === 'tool_call_invocation' || item.role === 'tool_call_result') {
                    timeToSeek = item.time_sec;
                }

                if (timeToSeek !== null) {
                    this.$refs.transcriptAudioPlayer.currentTime = timeToSeek;
                    this.$refs.transcriptAudioPlayer.play();
                }
            }
        },

        formatSeconds(seconds) {
            if (!seconds && seconds !== 0) return '';
            var mins = Math.floor(seconds / 60);
            var secs = Math.floor(seconds % 60);
            return mins + ':' + (secs < 10 ? '0' + secs : secs);
        },

        getToolIcon(type) {
            var icons = {
                'end_call': 'call_end',
                'transfer_call': 'call_forward',
                'press_digit': 'dialpad',
                'extract_dynamic_variable': 'data_object',
                'agent_swap': 'swap_horiz',
                'custom': 'build'
            };
            return icons[type] || 'extension';
        },

        getToolTypeColor(type) {
            var colors = {
                'end_call': 'red',
                'transfer_call': 'blue',
                'press_digit': 'orange',
                'extract_dynamic_variable': 'purple',
                'agent_swap': 'cyan',
                'custom': 'green'
            };
            return colors[type] || 'default';
        },

        getToolTypeText(type) {
            var texts = {
                'end_call': 'End Call',
                'transfer_call': 'Transfer',
                'press_digit': 'IVR',
                'extract_dynamic_variable': 'Extract Variable',
                'agent_swap': 'Agent Transfer',
                'custom': 'Custom Function'
            };
            return texts[type] || type;
        },

        formatToolArguments(args) {
            if (!args) return '';
            try {
                var parsed = typeof args === 'string' ? JSON.parse(args) : args;
                return JSON.stringify(parsed, null, 2);
            } catch (e) {
                return args;
            }
        },

        getAutomationLink(item) {
            // Sadece custom function için otomasyon linki oluştur
            if (item.type !== 'custom') return null;

            // Agent'ın tool listesinden bu fonksiyonu bul
            var agents = this.agents || [];
            for (var i = 0; i < agents.length; i++) {
                var agent = agents[i];
                if (!agent.llm || !agent.llm.general_tools) continue;
                
                var tools = agent.llm.general_tools;
                for (var j = 0; j < tools.length; j++) {
                    var tool = tools[j];
                    if (tool.name === item.name && tool.type === 'custom' && tool.url) {
                        // Webhook automation URL pattern: /webhook/automation/{siteId}/{webhookId}
                        var webhookPattern = /\/webhook\/automation\/([^\/]+)\/([^\/]+)/;
                        var match = tool.url.match(webhookPattern);
                        if (match) {
                            var automationId = this.findAutomationIdByWebhookId(match[2]);
                            if (automationId) {
                                return '/automations/' + automationId + '/design';
                            }
                        }
                    }
                }
            }
            return null;
        },

        findAutomationIdByWebhookId(webhookId) {
            // Store'dan otomasyonları kontrol et
            var automations = this.$store.state.automation ? this.$store.state.automation.automations : [];
            if (!automations || !Array.isArray(automations)) return null;

            for (var i = 0; i < automations.length; i++) {
                var automation = automations[i];
                var autoWebhookId = this.$_.get(automation, 'options.webhook.id');
                if (autoWebhookId === webhookId) {
                    return automation._id;
                }
            }
            return null;
        },

        prepareTranscriptTimestamps() {
            // Transcript_object verisinden timestamp bilgilerini hazırla
            this.transcriptTimestamps = [];

            if (this.selectedCall && this.selectedCall.transcript_object) {
                this.selectedCall.transcript_object.forEach((entry, entryIndex) => {
                    if (entry.words && entry.words.length > 0) {
                        // Her bir mesaj için başlangıç zamanını kullan
                        this.transcriptTimestamps[entryIndex] = entry.words[0].start;
                    }
                });
            }
        },

        exportData() {
            const columns = this.visibleColumns.map((col) => col.title);
            let csvContent = columns.join(",") + "\n";

            this.calls.forEach((call) => {
                const row = this.visibleColumns.map((col) => {
                    const key = col.dataIndex;
                    if (key === "start_timestamp") {
                        return this.formatDate(call.start_timestamp);
                    } else if (col.key === "callDuration") {
                        return this.formatDuration(call.start_timestamp, call.end_timestamp);
                    } else if (key === "call_type") {
                        return call.call_type;
                    } else if (col.key === "direction") {
                        return call.direction === "inbound" ? "Inbound" : "Outbound";
                    } else if (col.key === "cost") {
                        return this.formatCost(call);
                    } else if (key === "call_id") {
                        return call.call_id;
                    } else if (key === "disconnection_reason") {
                        return this.formatDisconnectionReason(call.disconnection_reason);
                    } else if (key === "call_status") {
                        return call.call_status;
                    } else if (col.key === "userSentiment") {
                        return $_.get(call, "call_analysis.user_sentiment", "Unknown");
                    } else if (key === "from_number") {
                        return call.from_number;
                    } else if (key === "to_number") {
                        return call.to_number;
                    } else if (key === "agent_id") {
                        return this.getAgentName(call.agent_id);
                    } else if (col.key === "callSuccessful") {
                        return $_.get(call, "call_analysis.call_successful") ? "Yes" : "No";
                    } else if (col.key === "latency") {
                        var latencyVal = $_.get(call, "latency.e2e.p50");
                        return latencyVal ? Math.round(latencyVal) + "ms" : "-";
                    }
                    return "";
                });
                csvContent += row.join(",") + "\n";
            });

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);

            link.setAttribute("href", url);
            link.setAttribute("download", `call-history-${moment().format("YYYY-MM-DD")}.csv`);
            link.style.visibility = "hidden";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        // URL parametrelerini işleme
        handleUrlParams() {
            const { query } = this.$route;

            // filter_batch_call_id parametresi varsa
            if (query.filter_batch_call_id) {
                // Önce direct olarak filters array'ine ekle
                const filterExists = this.filters.some((f) => f.name === "batch_call_id");

                if (!filterExists) {
                    const batchCallFilter = {
                        name: "batch_call_id",
                        filterValue: query.filter_batch_call_id,
                        materialIcon: "batch_prediction",
                        label: "Toplu Arama ID",
                    };

                    this.filters.push(batchCallFilter);

                    // FilterToolbar'a batch_call_id parametresini ekle
                    this.$nextTick(() => {
                        if (this.$refs.filterToolbar) {
                            this.$refs.filterToolbar.addFilter("batch_call_id", query.filter_batch_call_id, false);
                        }
                    });
                }
            }
        },
    },
    created() {
        this.loadSettings();
        this.fetchAgents();
    },
    mounted() {
        // URL'den parametreleri al ve filterToolbar'a ilet
        this.handleUrlParams();
        this.fetchCalls();
    },
    beforeDestroy() {
        // Sayfa kapanırken ses oynatıcıyı temizle
        this.closeAudioPlayer();
    },
};
</script>

<style scoped lang="scss">
.voice-agent-page-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 0 20px;
    position: relative;
    height: calc(100vh - 100px);
    max-height: calc(100vh - 100px);
    overflow: hidden;

    .header-section {
        margin-bottom: 5px;
    }

    .title-wrapper {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-bottom: 20px;
    }

    .main-title {
        font-size: 24px;
        font-weight: 500;
        margin: 0;
    }

    .title-description {
        font-size: 14px;
        font-weight: 400;
        color: #8c8c8c;
        margin: 0;
    }

    .section {
        display: flex;
        flex-direction: column;
        gap: 15px;
        height: 100%;
        overflow: hidden;
        margin-bottom: 0px !important;
    }

    .toolbar-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 0;
        border-bottom: 1px solid #e8e8e8;
        flex-shrink: 0;
    }

    .toolbar-group {
        display: flex;
        gap: 10px;
        align-items: center;
    }

    .toolbar-button {
        display: flex;
        align-items: center;
        gap: 5px;

        i {
            font-size: 16px;
        }
    }

    .table-wrapper {
        flex: 1;
        overflow: hidden;
        width: 100%;

        :deep(.ant-spin-nested-loading),
        :deep(.ant-spin-container),
        :deep(.ant-table-wrapper),
        :deep(.ant-table) {
            height: 100%;
        }

        :deep(.ant-table-header) {
            overflow-y: scroll !important;
            position: sticky;
            top: 0;
            z-index: 2;
            background: white;
        }

        :deep(.ant-table-body) {
            overflow-y: scroll !important;
            max-height: calc(100vh - 300px) !important;
        }
    }

    .simple-pagination {
        display: flex;
        align-items: center;
        gap: 8px;

        .page-number {
            font-size: 16px;
            font-weight: 500;
            min-width: 30px;
            text-align: center;
        }
    }

    .audio-player-dock {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 15px 0;
        // background: linear-gradient(90deg, #3a7bd5 0%, #00d2ff 100%);
        // box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.2);
        z-index: 1000;

        .audio-player-container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            max-width: 800px;
            margin: 0 auto;
            padding: 15px 25px;
            background: rgb(255 229 224);
            border-radius: 12px;

            .audio-player-info {
                display: flex;
                align-items: center;
                gap: 12px;
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;

                .audio-icon {
                    font-size: 28px;
                    color: #3a7bd5;
                    background: white;
                    border-radius: 50%;
                    padding: 8px;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
                    animation: pulse 2s infinite;
                }

                .audio-title-container {
                    .now-playing {
                        font-weight: 600;
                        color: #3a7bd5;
                        font-size: 12px;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        margin-bottom: 3px;
                    }

                    .audio-title {
                        font-weight: 500;
                        color: #333;
                        font-size: 14px;
                    }
                }
            }

            .audio-player-wrapper {
                flex: 2;
                max-width: 500px;
                padding: 0 20px;

                audio {
                    width: 100%;
                    height: 40px;
                    border-radius: 8px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
            }

            .audio-player-controls {
                margin-left: 15px;

                button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
                    transition: all 0.3s ease;

                    &:hover {
                        transform: scale(1.1);
                        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
                    }
                }
            }
        }
    }

    @keyframes pulse {
        0% {
            transform: scale(1);
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        }
        50% {
            transform: scale(1.05);
            box-shadow: 0 6px 15px rgba(58, 123, 213, 0.4);
        }
        100% {
            transform: scale(1);
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        }
    }
}

.transcript-container {
    margin-top: 20px;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: 15px;
    background-color: #f9f9f9;

    h3 {
        margin-top: 0;
        margin-bottom: 10px;
    }

    .transcript-content {
        max-height: 700px;
        overflow-y: auto;
    }

    .transcript-item {
        padding: 10px;
        margin-bottom: 8px;
        border-radius: 8px;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            opacity: 0.9;
        }

        &:last-child {
            margin-bottom: 0;
        }

        .message-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 6px;

            .material-icons {
                font-size: 18px;
            }

            .role-label {
                font-weight: 600;
                font-size: 13px;
            }

            .time-label {
                font-size: 11px;
                color: #888;
                margin-left: auto;
            }
        }

        .message-content {
            font-size: 14px;
            line-height: 1.5;
            padding-left: 26px;
        }
    }

    .transcript-user {
        background-color: #e3f2fd;
        border-left: 3px solid #2196f3;

        .message-header .material-icons {
            color: #2196f3;
        }
    }

    .transcript-agent {
        background-color: #e8f5e9;
        border-left: 3px solid #4caf50;

        .message-header .material-icons {
            color: #4caf50;
        }
    }

    .transcript-tool_call_invocation,
    .transcript-tool_call_result {
        padding: 0;
        background: transparent;
    }

    .tool-call-box {
        border-radius: 6px;
        overflow: hidden;

        &.invocation {
            background-color: #fff3e0;
            border: 1px solid #ffb74d;

            .tool-call-header {
                background-color: #ffe0b2;
            }
        }

        &.result {
            background-color: #f5f5f5;
            border: 1px solid #e0e0e0;

            &.success {
                background-color: #e8f5e9;
                border-color: #a5d6a7;

                .tool-call-header {
                    background-color: #c8e6c9;

                    .material-icons {
                        color: #4caf50;
                    }
                }
            }

            &.error {
                background-color: #ffebee;
                border-color: #ef9a9a;

                .tool-call-header {
                    background-color: #ffcdd2;

                    .material-icons {
                        color: #f44336;
                    }
                }
            }
        }

        .tool-call-header {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 12px;
            flex-wrap: wrap;

            .material-icons {
                font-size: 18px;
                color: #ff9800;
            }

            .tool-name {
                font-weight: 600;
                font-size: 13px;
                color: #333;
            }

            .result-label {
                font-weight: 600;
                font-size: 13px;
            }

            .time-label {
                font-size: 11px;
                color: #666;
                margin-left: auto;
            }

            .automation-link {
                display: flex;
                align-items: center;
                gap: 4px;
                font-size: 12px;
                color: #1890ff;
                text-decoration: none;
                padding: 2px 8px;
                background: rgba(24, 144, 255, 0.1);
                border-radius: 4px;
                transition: all 0.2s;

                &:hover {
                    background: rgba(24, 144, 255, 0.2);
                }

                .material-icons {
                    font-size: 14px;
                    color: #1890ff;
                }
            }
        }

        .tool-call-arguments,
        .tool-call-content {
            padding: 8px 12px;

            pre {
                margin: 0;
                font-size: 12px;
                white-space: pre-wrap;
                word-break: break-word;
                background: rgba(0, 0, 0, 0.03);
                padding: 8px;
                border-radius: 4px;
                max-height: 150px;
                overflow-y: auto;
            }
        }
    }
}

.call-details {
    .call-info {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 10px;
        margin-bottom: 20px;
    }

    .call-summary,
    .audio-player {
        margin-top: 20px;
    }
}

.modern-audio-player {
    display: flex;
    flex-direction: column;
    background: rgb(255 229 224);
    border-radius: 8px;
    padding: 12px;
    margin-top: 10px;

    .audio-player-info {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 10px;

        .audio-icon {
            font-size: 22px;
            color: #3a7bd5;
            background: white;
            border-radius: 50%;
            padding: 6px;
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
            animation: pulse 2s infinite;
        }

        .audio-title-container {
            .audio-title {
                font-weight: 500;
                color: #333;
                font-size: 14px;
            }
        }
    }

    .audio-player-wrapper {
        audio {
            border-radius: 20px;
            height: 36px;
        }
    }
}

@keyframes pulse {
    0% {
        transform: scale(1);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    }
    50% {
        transform: scale(1.05);
        box-shadow: 0 6px 15px rgba(58, 123, 213, 0.4);
    }
    100% {
        transform: scale(1);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    }
}
</style>
<style lang="scss">
.customize-columns-container {
    max-height: 500px;
    overflow-y: auto;

    .column-option {
        display: flex;

        justify-content: space-between;
        align-items: center;
        padding: 10px 10px;
        border-bottom: 1px solid #f0f0f0;
        &:hover {
            background-color: #f0f0f0;
            cursor: pointer;
        }

        &:last-child {
            border-bottom: none;
        }

        .column-label {
            font-weight: 500;
        }
    }
}
</style>
